import { 
  getFirestore, doc, setDoc, getDoc, updateDoc, deleteDoc, 
  collection, getDocs, query, where 
} from "firebase/firestore";

const db = getFirestore();

// ==================== Product Class ====================
class Product {
  constructor(name, soldPrice, restaurantId, image, ingredients, category = "") {
    this.name = name;
    this.soldPrice = soldPrice;
    this.restaurantId = restaurantId;
    this.image = image;
    this.ingredients = ingredients; // [{ stockName: "Tomato", quantity: 0.2 }]
    this.category = category; // New category field
    this.costPrice = 0;
  }

  async calculateAndUpdateCost() {
    try {
      let totalCost = 0;
      for (const item of this.ingredients) {
        const stockQuery = query(
          collection(db, "stocks"), 
          where("productName", "==", item.stockName),
          where("restaurantId", "==", this.restaurantId)
        );
        const stockSnap = await getDocs(stockQuery);
        if (!stockSnap.empty) {
          const stockData = stockSnap.docs[0].data();
          totalCost += item.quantity * stockData.pricePerUnit;
        }
      }
      this.costPrice = totalCost;

      // Update product cost in Firestore
      if (this.id) {
        const productRef = doc(db, "products", this.id);
        await updateDoc(productRef, { costPrice: this.costPrice });
      }

      console.log(`Updated cost price for ${this.name}: ${this.costPrice}`);
      return totalCost;
    } catch (error) {
      console.error("Error calculating product cost:", error.message);
      throw error;
    }
  }

  async addProduct() {
    try {
      this.costPrice = await this.calculateAndUpdateCost();
      const productRef = doc(collection(db, "products"));
      await setDoc(productRef, {
        name: this.name,
        soldPrice: this.soldPrice,
        restaurantId: this.restaurantId,
        image: this.image,
        ingredients: this.ingredients,
        costPrice: this.costPrice,
        category: this.category, // Add category to Firestore
        createdAt: new Date().toISOString(), // Optional: add timestamp
        updatedAt: new Date().toISOString()  // Optional: add timestamp
      });
      console.log("Product added successfully!");
    } catch (error) {
      console.error("Error adding product:", error.message);
      throw error;
    }
  }

  async updateProduct(productId, updatedData) {
    try {
      if (updatedData.ingredients) {
        this.ingredients = updatedData.ingredients;
        updatedData.costPrice = await this.calculateAndUpdateCost();
      }
      
      // Add updated timestamp
      updatedData.updatedAt = new Date().toISOString();
      
      const productRef = doc(db, "products", productId);
      await updateDoc(productRef, updatedData);
      console.log("Product updated successfully!");
    } catch (error) {
      console.error("Error updating product:", error.message);
      throw error;
    }
  }

  async deleteProduct(productId) {
    try {
      const productRef = doc(db, "products", productId);
      await deleteDoc(productRef);
      console.log("Product deleted successfully!");
    } catch (error) {
      console.error("Error deleting product:", error.message);
      throw error;
    }
  }

  static async getAllProducts() {
    try {
      const productsRef = collection(db, "products");
      const productDocs = await getDocs(productsRef);
      return productDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting products:", error.message);
      throw error;
    }
  }

  // New method to get products by category
  static async getProductsByCategory(restaurantId, category) {
    try {
      const productsRef = collection(db, "products");
      const q = query(
        productsRef, 
        where("restaurantId", "==", restaurantId),
        where("category", "==", category)
      );
      const productDocs = await getDocs(q);
      return productDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error("Error getting products by category:", error.message);
      throw error;
    }
  }

  // New method to get all unique categories for a restaurant
  static async getCategories(restaurantId) {
    try {
      const productsRef = collection(db, "products");
      const q = query(productsRef, where("restaurantId", "==", restaurantId));
      const productDocs = await getDocs(q);
      const categories = new Set();
      productDocs.docs.forEach(doc => {
        const category = doc.data().category;
        if (category && category.trim() !== "") {
          categories.add(category);
        }
      });
      return Array.from(categories).sort();
    } catch (error) {
      console.error("Error getting categories:", error.message);
      throw error;
    }
  }
}

export default Product;